import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  notificationService,
  NotificationConfig,
  EVENT_TYPE_LABELS,
  NOTIFICATION_TYPE_LABELS,
} from '@/services/notificationService';
import toast from 'react-hot-toast';
import {
  BellIcon,
  EnvelopeIcon,
  DevicePhoneMobileIcon,
  CheckCircleIcon,
  XCircleIcon,
  PaperAirplaneIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';

/**
 * Page de configuration des notifications
 */
export function NotificationsPage() {
  const queryClient = useQueryClient();
  const [testEmail, setTestEmail] = useState('');
  const [testPhone, setTestPhone] = useState('');

  // Chargement des configurations
  const { data: configs, isLoading } = useQuery({
    queryKey: ['notification-configs'],
    queryFn: () => notificationService.listConfigs(),
  });

  // Statut du systeme
  const { data: status } = useQuery({
    queryKey: ['notification-status'],
    queryFn: () => notificationService.getStatus(),
  });

  // Mutations
  const toggleMutation = useMutation({
    mutationFn: (id: number) => notificationService.toggleConfig(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['notification-configs'] });
      queryClient.invalidateQueries({ queryKey: ['notification-status'] });
      toast.success(data.message);
    },
    onError: () => toast.error('Erreur lors de la modification'),
  });

  const initMutation = useMutation({
    mutationFn: () => notificationService.initConfigs(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['notification-configs'] });
      queryClient.invalidateQueries({ queryKey: ['notification-status'] });
      toast.success(data.message);
    },
    onError: () => toast.error('Erreur lors de l\'initialisation'),
  });

  const testMutation = useMutation({
    mutationFn: ({ type, recipient }: { type: string; recipient: string }) =>
      notificationService.sendTest(type, recipient),
    onSuccess: (data) => {
      toast.success(data.message);
    },
    onError: () => toast.error('Erreur lors de l\'envoi du test'),
  });

  const handleTestEmail = () => {
    if (!testEmail) {
      toast.error('Veuillez saisir une adresse email');
      return;
    }
    testMutation.mutate({ type: 'email', recipient: testEmail });
  };

  const handleTestSms = () => {
    if (!testPhone) {
      toast.error('Veuillez saisir un numero de telephone');
      return;
    }
    testMutation.mutate({ type: 'sms', recipient: testPhone });
  };

  // Grouper les configs par evenement
  const configsByEvent = configs?.reduce((acc, config) => {
    const event = config.eventType;
    if (!acc[event]) acc[event] = {};
    acc[event][config.notificationType] = config;
    return acc;
  }, {} as Record<string, Record<string, NotificationConfig>>);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
        <p className="text-sm text-gray-500 mt-3">Chargement des notifications...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Configuration des notifications</h2>
        <button onClick={() => initMutation.mutate()} className="btn-secondary">
          <Cog6ToothIcon className="w-5 h-5 mr-2" />
          Initialiser config
        </button>
      </div>

      {/* Statut systeme */}
      {status && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="card">
            <div className="card-body">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <EnvelopeIcon className="w-6 h-6 text-primary-500 mr-3" />
                  <div>
                    <h3 className="font-semibold">Email</h3>
                    <p className="text-sm text-gray-500">{status.email.provider}</p>
                  </div>
                </div>
                <span
                  className={`px-2 py-1 text-xs rounded-full ${
                    status.email.configured
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}
                >
                  {status.email.status}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="Email de test"
                  className="input flex-1"
                />
                <button
                  onClick={handleTestEmail}
                  disabled={testMutation.isPending}
                  className="btn-primary"
                >
                  <PaperAirplaneIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <DevicePhoneMobileIcon className="w-6 h-6 text-primary-500 mr-3" />
                  <div>
                    <h3 className="font-semibold">SMS</h3>
                    <p className="text-sm text-gray-500">{status.sms.provider}</p>
                  </div>
                </div>
                <span
                  className={`px-2 py-1 text-xs rounded-full ${
                    status.sms.configured
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}
                >
                  {status.sms.status}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="tel"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  placeholder="+33612345678"
                  className="input flex-1"
                />
                <button
                  onClick={handleTestSms}
                  disabled={testMutation.isPending || !status.sms.configured}
                  className="btn-primary"
                >
                  <PaperAirplaneIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      {status && (
        <div className="grid grid-cols-3 gap-4">
          <div className="card">
            <div className="card-body text-center">
              <p className="text-2xl font-bold text-gray-900">
                {status.configurations.total}
              </p>
              <p className="text-sm text-gray-500">Configurations</p>
            </div>
          </div>
          <div className="card">
            <div className="card-body text-center">
              <p className="text-2xl font-bold text-green-600">
                {status.configurations.actives}
              </p>
              <p className="text-sm text-gray-500">Actives</p>
            </div>
          </div>
          <div className="card">
            <div className="card-body text-center">
              <p className="text-2xl font-bold text-gray-400">
                {status.configurations.inactives}
              </p>
              <p className="text-sm text-gray-500">Inactives</p>
            </div>
          </div>
        </div>
      )}

      {/* Tableau des configurations par evenement */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Evenement</th>
                <th className="text-center">Email</th>
                <th className="text-center">SMS</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {Object.entries(EVENT_TYPE_LABELS).map(([eventType, label]) => {
                const eventConfigs = configsByEvent?.[eventType] || {};
                const emailConfig = eventConfigs['email'];
                const smsConfig = eventConfigs['sms'];

                return (
                  <tr key={eventType}>
                    <td>
                      <div className="flex items-center">
                        <BellIcon className="w-5 h-5 text-gray-400 mr-3" />
                        <div>
                          <p className="font-medium">{label}</p>
                          <p className="text-xs text-gray-500">{eventType}</p>
                        </div>
                      </div>
                    </td>
                    <td className="text-center">
                      {emailConfig ? (
                        <button
                          onClick={() => toggleMutation.mutate(emailConfig.id)}
                          className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
                            emailConfig.enabled
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {emailConfig.enabled ? (
                            <CheckCircleIcon className="w-4 h-4 mr-1" />
                          ) : (
                            <XCircleIcon className="w-4 h-4 mr-1" />
                          )}
                          {emailConfig.enabled ? 'Actif' : 'Inactif'}
                        </button>
                      ) : (
                        <span className="text-gray-400 text-sm">Non configure</span>
                      )}
                    </td>
                    <td className="text-center">
                      {smsConfig ? (
                        <button
                          onClick={() => toggleMutation.mutate(smsConfig.id)}
                          className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
                            smsConfig.enabled
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {smsConfig.enabled ? (
                            <CheckCircleIcon className="w-4 h-4 mr-1" />
                          ) : (
                            <XCircleIcon className="w-4 h-4 mr-1" />
                          )}
                          {smsConfig.enabled ? 'Actif' : 'Inactif'}
                        </button>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Note */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> Les notifications SMS necessitent la configuration d'AWS SNS.
          Seules les interventions de priorite "Critique" declenchent l'envoi de SMS (R10).
        </p>
      </div>
    </div>
  );
}

export default NotificationsPage;
